import {DatasetDef, FieldDef, FieldType, GeoFieldType, RowCell} from "../types";
import {deepEqual, isValidNumber, isValueDefined} from "../util/CommonUtil";
import {getAppState} from "../store/AppState";


export enum DatasetInfoFieldType {
    valueField,
    nameField,
    latField,
    lonField,
    geoField,
    descField,
    identField,
    countryField,
    stateField,
    countyField,
    headingField,
}

/**
 * Class used to manage all the dataset meta information and provide other useful features.
 */
export class DatasetInfo {
    // datasetDef is only used to pass info into this class
    private readonly datasetDef: DatasetDef;

    private name: string;

    private fieldDefsById: Map<string, FieldDef> = new Map<string, FieldDef>();
    private fieldIndexesById: Map<string, number> = new Map<string, number>();

    // indices of some important fields
    private fieldIndices: Map<DatasetInfoFieldType, number> = new Map<DatasetInfoFieldType, number>();

    constructor(datasetDef: DatasetDef) {
        this.datasetDef = datasetDef;

        this.name = datasetDef.info.name;

        for (let i = 0; i < datasetDef.fields.length; i++) {
            const f = datasetDef.fields[i];
            let id = f.id?.toLowerCase();
            if (id) {
                this.fieldDefsById.set(id, f);
                this.fieldIndexesById.set(id, i);
            }
        }

        for (const t of  Object.values(DatasetInfoFieldType)) {
            const ft = t as DatasetInfoFieldType;
            const fieldId = this.getFieldId(ft);
            this.fieldIndices.set(ft, this.fieldIndexesById.get(fieldId));
        }
    }

    getId = () => this.datasetDef.id;

    getName = () => this.name;

    setName = (name: string) => this.name = name;

    getType = () => this.datasetDef.info.type;

    getFieldDefs = (): FieldDef[] => [...this.datasetDef.fields];

    getFieldIndex = (infoFieldType: DatasetInfoFieldType): number => {
        return this.fieldIndices.get(infoFieldType);
    }

    getFieldId = (infoFieldType: DatasetInfoFieldType): string => {
        switch (infoFieldType) {
            case DatasetInfoFieldType.valueField: return this.datasetDef.info.valueField;
            case DatasetInfoFieldType.nameField: return this.datasetDef.info.nameField;
            case DatasetInfoFieldType.latField: return this.datasetDef.info.latField;
            case DatasetInfoFieldType.lonField: return this.datasetDef.info.lonField;
            case DatasetInfoFieldType.geoField: return this.datasetDef.info.geoField;
            case DatasetInfoFieldType.descField: return this.datasetDef.info.descField;
            case DatasetInfoFieldType.identField: return this.datasetDef.info.identField;
            case DatasetInfoFieldType.countryField: return this.datasetDef.info.countryField;
            case DatasetInfoFieldType.stateField: return this.datasetDef.info.stateField;
            case DatasetInfoFieldType.countyField: return this.datasetDef.info.countyField;
            case DatasetInfoFieldType.headingField: return this.datasetDef.info.headingField;
        }
    };

    getLonField = () => this.datasetDef.info.lonField;

    getCountryField = () => this.datasetDef.info.countryField;

    getStateField = () => this.datasetDef.info.stateField;

    getCountyField = () => this.datasetDef.info.countyField;

    getHeadingField = () => this.datasetDef.info.headingField;

    getFieldIndexById = (fieldId: string): number => {
        return fieldId ? this.fieldIndexesById.get(fieldId.toLowerCase()) : undefined;
    }

    getFieldDefById = (fieldId: string): FieldDef => {
        return fieldId ? this.fieldDefsById.get(fieldId.toLowerCase()) : undefined;
    }

    getFirstFieldDefByType = (fieldType: FieldType): FieldDef => {
        for (const def of this.datasetDef.fields) {
            if (def.type === fieldType) {
                return def;
            }
        }
        return null;
    }
}


/**
 * Single dataset row. Should be used to get any information about any dataset row.
 */
export class DatasetRow {

    private rowData: RowCell[];
    private datasetInfo: DatasetInfo;

    constructor(rowData: RowCell[], fieldsManager: DatasetInfo) {
        this.rowData = rowData;
        this.datasetInfo = fieldsManager;
    }

    getRowData = () => this.rowData;

    public getLat = (): number => {
        let lat = this.rowData[this.datasetInfo.getFieldIndex(DatasetInfoFieldType.latField)];
        return isValidNumber(lat) ? lat as number : NaN;
    }

    public getLon = (): number => {
        let lon = this.rowData[this.datasetInfo.getFieldIndex(DatasetInfoFieldType.lonField)] as number;
        return isValidNumber(lon) ? lon as number : NaN;
    }

    public getLonLat = (): [number, number] => {
        return [this.getLon(), this.getLat()];
    }

    public getIdent = (): string => {
        return this.rowData[this.datasetInfo.getFieldIndex(DatasetInfoFieldType.identField)] as string;
    }

    public getDesc = (): string => {
        return this.rowData[this.datasetInfo.getFieldIndex(DatasetInfoFieldType.descField)] as string;
    }

    public getCountryStateCounty = (): [string, string, string] => {
        const ensureNA = (s: string): string => {
            return s?.trim().toLowerCase() === "n/a" ? null : s;
        }
        return [
            ensureNA(this.rowData[this.datasetInfo.getFieldIndex(DatasetInfoFieldType.countryField)] as string),
            ensureNA(this.rowData[this.datasetInfo.getFieldIndex(DatasetInfoFieldType.stateField)] as string),
            ensureNA(this.rowData[this.datasetInfo.getFieldIndex(DatasetInfoFieldType.countyField)] as string),
        ];
    }

    public getValue = (fieldId: string): any => {
        let index = this.datasetInfo.getFieldIndexById(fieldId);
        return index >= 0 ? this.rowData[index] : undefined;
    }

    public getNumberValue = (fieldId: string): number => {
        let index = this.datasetInfo.getFieldIndexById(fieldId);
        if (index >= 0) {
            const value = this.rowData[index];
            if (isValidNumber(value)) {
                return Number(value);
            }
        }
        return undefined;
    }

    public hasSomeGeoFieldValue = (): boolean => {
        const hasValue = (fieldId: string) => fieldId && isValueDefined(this.getValue(fieldId));

        return isValueDefined(this.getLat()) ||
            hasValue(this.datasetInfo.getFieldId(DatasetInfoFieldType.countryField)) ||
            hasValue(this.datasetInfo.getFieldId(DatasetInfoFieldType.stateField)) ||
            hasValue(this.datasetInfo.getFieldId(DatasetInfoFieldType.countyField));
    }

    public hasValidLatLon = (): boolean => {
        return isValidNumber(this.getLat()) && isValidNumber(this.getLon());
    }

}

export type DatasetRowsByGeometry = {
    country: string;
    state: string;
    county: string;
    dataRows: DatasetRow[];
}


/**
 * Whole dataset which contains data and fields infos.
 */
export class Dataset {

    private readonly datasetDef: DatasetDef;
    private data: RowCell[][];
    private readonly datasetInfo: DatasetInfo;

    private id: string;

    constructor(datasetDef: DatasetDef, data: RowCell[][] = null) {
        this.datasetDef = datasetDef;
        this.data = data;
        this.datasetInfo = new DatasetInfo(datasetDef);
        this.id = getAppState().getUniqueDatasetId(datasetDef.id);
    }

    saveDatasetDef = (): DatasetDef => {
        const def = {...this.datasetDef, id: this.id};

        const symField = this.datasetInfo.getFieldId(DatasetInfoFieldType.identField);
        def.info.symbols = this.getFieldDistinctValues(symField);

        return def;
    };

    getDatasetInfo = (): DatasetInfo => this.datasetInfo;

    isDataLoaded = (): boolean => isValueDefined(this.data);

    public setData = (data: RowCell[][]) => {
        this.data = data;
    }

    public getId = () => {
        return this.id;
    }

    public setName = (name: string) => {
        return this.datasetInfo.setName(name);
    }

    public getName = () => {
        return this.datasetInfo.getName();
    }

    public getType = (): string => {
        return this.datasetInfo.getType();
    }

    public getSize = (): number => {
        return this.data?.length ?? 0;
    }

    public getDataRow = (index: number): DatasetRow => {
        if (this.data && index >= 0 && index < this.getSize()) {
            return new DatasetRow(this.data[index], this.datasetInfo);
        }
        return null;
    }

    public getDataRows = (): DatasetRow[] => {
        return this.data?.map(row => new DatasetRow(row, this.datasetInfo)) ?? [];
    }

    public countGeoFieldDataRows = (): number => {
        let count = 0;
        this.getDataRows().forEach(row => {
            if (row.hasSomeGeoFieldValue()) {
                count++;
            }
        });
        return count;
    }

    public getFieldValues = (fieldId: string): any[] => {
        if (this.isDataLoaded()) {
            const idx = this.datasetInfo.getFieldIndexById(fieldId);
            const values = [];
            if (idx >= 0) {
                for (const row of this.data) {
                    values.push(row[idx]);
                }
            }

            return values;
        }
        return [];
    }

    supportsGeojson = (): boolean => {
        let info = this.datasetDef.info;
        return !!info.countryField || !!info.stateField || !!info.countyField;
    }

    getFieldDistinctValues = (fieldId: string): any[] => {
        return Array.from(new Set(this.getFieldValues(fieldId))).sort();
    }

    public createBucketsByField = (fieldId: string, bucketCount: number): number[] => {
        const values = this.getFieldValues(fieldId).filter(isValidNumber).sort((a, b) => {
            a = a ?? 0;
            b = b ?? 0;
            return a > b ? 1 : (a < b ? -1 : 0);
        });
        const buckets = [];
        if (bucketCount > 0) {
            const bucketSize = Math.max(1, values.length / bucketCount);
            let currIdx = 0;
            let last = NaN;
            while (currIdx < values.length && buckets.length < bucketCount) {
                const next = values[currIdx] ?? 0;
                if (next !== last) {
                    buckets.push(next);
                    last = next;
                }
                currIdx = Math.round(currIdx + bucketSize);
            }
            buckets.push(values[values.length - 1]);
        }

        return buckets;
    }

    public getMinMax = (fieldId: string): [number, number] => {
        if (this.data && fieldId) {
            const field = this.datasetInfo.getFieldDefById(fieldId);
            if (field && (field.type === "real" || field.type === "integer")) {
                const index = this.datasetInfo.getFieldIndexById(fieldId);
                if (index >= 0) {
                    let [min, max] = [undefined, undefined];
                    for (const row of this.data) {
                        const value = row[index];
                        if (isValidNumber(value)) {
                            const num = Number(value);
                            if (min === undefined || num < min) {
                                min = num;
                            }
                            if (max === undefined || num > max) {
                                max = num;
                            }
                        }
                    }
                    return [min, max];
                }
            }
        }
        return [undefined, undefined];
    }

    getFieldDefs = (): FieldDef[] => {
        return this.datasetInfo.getFieldDefs();
    }

    getFirstFloatFieldDef = (): FieldDef => {
        return this.datasetInfo.getFirstFieldDefByType("real");
    }

    getFirstIntegerFieldDef = (): FieldDef => {
        return this.datasetInfo.getFirstFieldDefByType("real");
    }

    getFieldDefById = (fieldId: string): FieldDef => {
        return this.datasetInfo.getFieldDefById(fieldId);
    }

    isCompatible = (other: Dataset): boolean => {
        return this.datasetInfo.getType() === other.datasetInfo.getType() &&
               deepEqual(this.datasetInfo.getFieldDefs(), other.datasetInfo.getFieldDefs());
    }

    collectDatasetRowsByGeometry = (geoFieldType: GeoFieldType): DatasetRowsByGeometry[] => {
        const map: Map<string, DatasetRowsByGeometry> = new Map<string, DatasetRowsByGeometry>();
        for (const row of this.getDataRows()) {
            let [country, state, county] = row.getCountryStateCounty();

            let match = !!country;
            if (geoFieldType === "country") {
                state = null;
                county = null;
            } else if (geoFieldType === "state") {
                match = !!country && !!state;
                county = null;
            } else if (geoFieldType === "county") {
                match = !!country && !!state && !!county;
            }

            if (match) {
                const key = (country?.toLowerCase() ?? "_") + "|" + (state?.toLowerCase() ?? "_") + "|" + (county?.toLowerCase() ?? "_");
                let datasetRowsByGeometry = map.get(key);
                if (!datasetRowsByGeometry) {
                    datasetRowsByGeometry = {country: country, state: state, county: county, dataRows: []};
                    map.set(key, datasetRowsByGeometry);
                }
                datasetRowsByGeometry.dataRows.push(row);
            }
        }
        const countGeometries = (d: DatasetRowsByGeometry): number => {
            let count = 0;
            if (d.country) {
                count++;
                if (d.state) {
                    count++;
                    if (d.county) {
                        count++;
                    }
                }
            }
            return count;
        }
        return Array.from(map.values()).sort((d1, d2) => {
            let n1 = countGeometries(d1);
            let n2 = countGeometries(d2);
            return n1 - n2;
        });
    }

    mergeDataset = (other: Dataset): void => {
        if (this.isDataLoaded() && this.isCompatible(other)) {
            const identFieldIdx = this.datasetInfo.getFieldIndex(DatasetInfoFieldType.identField);
            if (identFieldIdx >= 0) {
                const symSet: Set<string> = new Set(this.getFieldValues(this.datasetInfo.getFieldId(DatasetInfoFieldType.identField)));
                for (const row of other.getDataRows()) {
                    const otherSym = row.getIdent();
                    if (!symSet.has(otherSym)) {
                        this.data.push(row.getRowData());
                        symSet.add(otherSym);
                    }
                }
            }
        }
    }

    calcBounds = (): [[number, number], [number, number]] => {
        let [minLon, maxLon] = this.getMinMax(this.datasetInfo.getFieldId(DatasetInfoFieldType.lonField));
        let [minLat, maxLat] = this.getMinMax(this.datasetInfo.getFieldId(DatasetInfoFieldType.latField));
        return [
            [minLon, minLat],
            [maxLon, maxLat]
        ];
    }

}